// framework/client/components/ApiKeyManager.js
import { ComponentBase } from "../core/ComponentBase.js";
import { z } from "zod"; // Import Zod

export class ApiKeyManager extends ComponentBase {
    constructor(config) {
        super(config);
        this.dataCrud = null;
    }

      async initialize() {
        const ApiKeyFormSchema = z.object({
            description: z.string().min(1, "A description is required."),
            initialContext: z.string().optional().nullable(),
        });

        this.dataCrud = await this.createManagedComponent("DataCrud", null, {
            instanceId: `${this.config.instanceId}-crud`,
            rpcClass: "ApiKeyRpc",
            schemaPath: "collections/apiKeys",
            title: "API Key Management",
            viewConfig: {
                mode: 'grid',
                fields: [
                    { name: 'description', label: 'Description' },
                    { name: 'prefix', label: 'Key Prefix', render: (val) => `<code>${val}...</code>` },
                    { name: 'lastUsedAt', label: 'Last Used', render: (val) => val ? new Date(val).toLocaleString() : 'Never' },
                    { name: 'isEnabled', label: 'Enabled', render: (val) => val ? '<i class="bi bi-check-circle-fill text-success"></i>' : '<i class="bi bi-x-circle text-muted"></i>' },
                ],
                toolbar: { create: { enabled: true, label: "Create New API Key" } }
            },
            formConfig: {
                schema: ApiKeyFormSchema,
                fields: {
                    description: { type: 'text', label: 'Description', required: true, placeholder: 'e.g., Marketing Automation Script' },
                    initialContext: {
                        type: 'textarea',
                        label: 'Initial Webhook Context (JSON)',
                        rows: 4,
                        placeholder: '{\n  "subscriptionId": "remote_app_sub_id",\n  "serviceCode": "remote_app_service_code"\n}',
                        helpText: "This JSON will be sent as 'initialContext' in webhooks from this application."
                    }
                }
            },
            dataConfig: { autoLoad: false }
        });

        this.dataCrud.dataProvider.on('dataInserted', (newKeyData) => {
            if (newKeyData.fullKey) {
                this.showNewKeyModal(newKeyData.fullKey);
            }
        });

        await super.initialize();
    }

    showNewKeyModal(fullKey) {
        const modalId = `new-api-key-modal-${this.config.instanceId}`;
        let modal = document.getElementById(modalId);
        if (modal) modal.remove();

        modal = document.createElement('div');
        modal.id = modalId;
        modal.className = 'modal fade';
        modal.innerHTML = `
            <div class="modal-dialog modal-dialog-centered">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">API Key Created Successfully</h5>
                        <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
                    </div>
                    <div class="modal-body">
                        <p class="text-danger"><strong>Important:</strong> This is the only time you will see this key. Please copy it and store it in a secure location.</p>
                        <div class="input-group">
                            <input type="text" class="form-control" value="${fullKey}" readonly>
                            <button class="btn btn-outline-secondary" id="copy-key-btn"><i class="bi bi-clipboard"></i> Copy</button>
                        </div>
                    </div>
                </div>
            </div>
        `;
        document.body.appendChild(modal);
        const bsModal = new bootstrap.Modal(modal);

        modal.querySelector('#copy-key-btn').addEventListener('click', () => {
            navigator.clipboard.writeText(fullKey);
            this.appClient.notifier.success("API Key copied to clipboard!");
        });

        modal.addEventListener('hidden.bs.modal', () => modal.remove(), { once: true });
        bsModal.show();
    }

    async initAfterLogin() {
        if (this.dataCrud) {
            this.dataCrud.refreshData();
        }
    }

    async render() {
        return await this.dataCrud.render();
    }
}